/**
 * Which rows of an obfuscated grid answer are real. Pure, unit-tested in `e2e/unit/gridFreeRows.spec.ts`;
 * `DataGrid.tsx` uses it to blur only the scrambled rows and to place the unlock banner over them.
 *
 * TED (`_handle_list_obfuscation`, interface/api/base/views.py) marks an obfuscated list with
 * `obfuscation: { obfuscated: true, columns, doNotObfuscateRows }`: the first `doNotObfuscateRows` rows are
 * returned as they are and every later row has its `columns` scrambled. A column the serializer marks
 * `alwaysObfuscate` is scrambled in the free rows too. Signed-out callers get 25 free rows on the
 * most-shorted lists (page 1 of the default sort); other obfuscated lists free their top 3 (1 when the
 * page has 10 rows or fewer). The old format (`obfuscated: true` at the top level) frees nothing.
 *
 * Everything here fails closed: a missing or malformed count frees no row, so a scrambled row is never
 * drawn unblurred.
 */

export interface GridObfuscation {
    /** The answer is obfuscated at all (the grid's lock: no sort, filter, column change or next page). */
    obfuscated: boolean;
    /** The columns TED scrambles; null for the old format (every column). */
    columns: string[] | null;
    /** How many leading rows TED returned unscrambled. */
    freeRows: number;
}

type RowId = string | number;

interface ObfuscatedAnswer {
    obfuscation?: { obfuscated?: unknown; columns?: unknown; doNotObfuscateRows?: unknown } | null;
    obfuscated?: unknown;
}

const NOT_OBFUSCATED: GridObfuscation = { obfuscated: false, columns: null, freeRows: 0 };

/** The obfuscation block of a grid answer, in either of TED's formats. */
export const readObfuscation = (res: unknown): GridObfuscation => {
    const answer = res as ObfuscatedAnswer | null;
    if (!answer || typeof answer !== "object") return NOT_OBFUSCATED;
    const block = answer.obfuscation;
    if (block && typeof block === "object") {
        const obfuscated = !!block.obfuscated;
        const columns = Array.isArray(block.columns) ? block.columns.filter((field): field is string => typeof field === "string") : [];
        const count = block.doNotObfuscateRows;
        const freeRows = obfuscated && typeof count === "number" && Number.isInteger(count) && count > 0 ? count : 0;
        return { obfuscated, columns, freeRows };
    }
    if (typeof answer.obfuscated === "boolean") return { obfuscated: answer.obfuscated, columns: null, freeRows: 0 };
    return NOT_OBFUSCATED;
};

/**
 * The ids of the rows TED returned unscrambled: the first `freeRows` of the page-1 answer, in the order it
 * sent them. An id that a scrambled row of the same answer also carries is left out (fails closed).
 */
export const freeRowIds = (rows: unknown, obfuscation: GridObfuscation): Set<RowId> => {
    const free = new Set<RowId>();
    if (!obfuscation.obfuscated || obfuscation.freeRows <= 0 || !Array.isArray(rows)) return free;
    const idOf = (row: unknown): RowId | null => {
        const id = (row as { id?: unknown } | null)?.id;
        return typeof id === "string" || typeof id === "number" ? id : null;
    };
    rows.slice(0, obfuscation.freeRows).forEach((row) => {
        const id = idOf(row);
        if (id !== null) free.add(id);
    });
    rows.slice(obfuscation.freeRows).forEach((row) => {
        const id = idOf(row);
        if (id !== null) free.delete(id);
    });
    return free;
};

/**
 * Whether one cell is drawn blurred. In an obfuscated answer every cell of a scrambled row is, as the grid
 * always drew them; a free row is readable except in an `alwaysObfuscate` column (scrambled there too).
 * The old format frees nothing.
 */
export const isCellScrambled = (obfuscation: GridObfuscation, rowIsFree: boolean, alwaysObfuscate = false): boolean => {
    if (!obfuscation.obfuscated) return false;
    if (obfuscation.columns === null || !rowIsFree) return true;
    return alwaysObfuscate;
};

/** The id of the first row TED scrambled, or null when every row is free (or there are no rows). */
export const firstScrambledRowId = (rows: unknown, free: Set<RowId>): RowId | null => {
    if (!Array.isArray(rows)) return null;
    for (const row of rows) {
        const id = (row as { id?: unknown } | null)?.id;
        if (typeof id !== "string" && typeof id !== "number") return null;
        if (!free.has(id)) return id;
    }
    return null;
};

export interface RenderedRow {
    id: RowId;
    /** Viewport top and bottom of the row's element. */
    top: number;
    bottom: number;
}

/**
 * Where the unlock banner starts, in viewport pixels, when the grid has free rows: the top of the first
 * scrambled row on screen, but never above the rows' visible area. Null when no scrambled row is on screen
 * (they are all further down; the reader scrolls to them), so no banner is drawn over free rows.
 */
export const lockedRegionTop = (rendered: RenderedRow[], free: Set<RowId>, viewport: { top: number; bottom: number }): number | null => {
    let top: number | null = null;
    for (const row of rendered) {
        if (free.has(row.id)) continue;
        if (row.bottom <= viewport.top || row.top >= viewport.bottom) continue;
        top = top === null ? row.top : Math.min(top, row.top);
    }
    return top === null ? null : Math.max(top, viewport.top);
};
